import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, AlertTriangle, Users, CalendarRange } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
/* eslint-disable react-hooks/rules-of-hooks */

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { const [y, m, d] = String(iso).split('T')[0].split('-'); return `${d}/${m}/${y}`; } catch { return iso; }
};

/** Reusable scanner dialog — pass `scope="leaves"` or `scope="employees"`. */
const DuplicateScannerDialog = ({ open, onOpenChange, scope = 'leaves' }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [merging, setMerging] = useState(null); // `${rejected_id}->${kept_id}`
  const token = localStorage.getItem('token');

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = scope === 'employees'
        ? `${API}/api/data-hygiene/employees/duplicates`
        : `${API}/api/data-hygiene/leaves/duplicates`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Scan failed';
      setError(detail);
      toast({ title: 'Duplicate scan failed', description: String(detail), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const mergeAndDelete = async (rejected, kept) => {
    const confirmMsg =
      `This will TRANSFER every HR record (leaves, advances, clock events, ` +
      `attendance, payroll, etc.) from\n\n  ${rejected.employee_code} · ${rejected.name}\n\n` +
      `to\n\n  ${kept.employee_code} · ${kept.name}\n\n` +
      `…and then PERMANENTLY DELETE the first record. This cannot be undone.\n\nContinue?`;
    if (!window.confirm(confirmMsg)) return;
    const mergeKey = `${rejected.id}->${kept.id}`;
    setMerging(mergeKey);
    try {
      const r = await axios.post(
        `${API}/api/data-hygiene/employees/${rejected.id}/merge-into/${kept.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const total = r.data?.transferred_total || 0;
      toast({
        title: 'Merged successfully',
        description: `Transferred ${total} record(s) to ${kept.employee_code} · ${kept.name}, then deleted ${rejected.employee_code}.`,
      });
      await runScan();
    } catch (e) {
      toast({
        title: 'Merge failed',
        description: e?.response?.data?.detail || e?.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setMerging(null);
    }
  };

  React.useEffect(() => {
    if (open && !data && !loading) runScan();
    if (!open) { setData(null); setError(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const title = scope === 'employees' ? 'Employee Duplicate Scan' : 'Leave Duplicate Scan';
  const Icon = scope === 'employees' ? Users : CalendarRange;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid={`${scope}-dupe-scan-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-rose-600" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-end mb-2">
          <Button size="sm" variant="outline" onClick={runScan} disabled={loading} data-testid={`${scope}-dupe-rescan`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Rescan
          </Button>
        </div>

        {loading && <p className="text-sm text-gray-500 text-center py-10">Scanning…</p>}
        {error && <p className="text-sm text-rose-600 text-center py-4">{String(error)}</p>}
        {data && !loading && (
          scope === 'leaves'
            ? <LeaveResults data={data} />
            : <EmployeeResults data={data} onMerge={mergeAndDelete} merging={merging} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const LeaveResults = ({ data }) => {
  const s = data.summary || {};
  const exact = data.exact_duplicates || [];
  const overlaps = data.overlaps || [];
  const clean = exact.length === 0 && overlaps.length === 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2 text-xs">
        <Tile label="Exact dupe groups" v={s.exact_dup_groups} tone="rose" />
        <Tile label="Exact dupe rows" v={s.exact_dup_rows} tone="rose" />
        <Tile label="Overlap pairs" v={s.overlap_pairs} tone="amber" />
        <Tile label="Employees affected" v={s.employees_affected} tone="violet" />
      </div>
      {clean ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
          ✓ No duplicate or overlapping leaves detected.
        </div>
      ) : (
        <>
          {exact.length > 0 && (
            <Section title={`Exact Duplicates (${exact.length} groups)`} tone="rose">
              <table className="w-full text-xs">
                <thead className="bg-rose-50 text-[10px] uppercase">
                  <tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Dates</th><th className="text-right p-2">Rows</th><th className="text-left p-2">Leave IDs</th></tr>
                </thead>
                <tbody>
                  {exact.map((g, i) => (
                    <tr key={i} className="border-t" data-testid={`leave-exact-row-${i}`}>
                      <td className="p-2 font-medium">{g.employee_name}</td>
                      <td className="p-2 font-mono">{fmtDate(g.start_date)} → {fmtDate(g.end_date)}</td>
                      <td className="p-2 text-right font-bold text-rose-600">{g.count}</td>
                      <td className="p-2 text-[10px] text-gray-500">{g.leaves.map(l => l.id?.slice(0, 8)).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
          {overlaps.length > 0 && (
            <Section title={`Overlapping Ranges (${overlaps.length} pairs)`} tone="amber">
              <table className="w-full text-xs">
                <thead className="bg-amber-50 text-[10px] uppercase">
                  <tr><th className="text-left p-2">Employee</th><th className="text-left p-2">Leave A</th><th className="text-left p-2">Leave B</th></tr>
                </thead>
                <tbody>
                  {overlaps.map((o, i) => (
                    <tr key={i} className="border-t" data-testid={`leave-overlap-row-${i}`}>
                      <td className="p-2 font-medium">{o.employee_name}</td>
                      <td className="p-2 font-mono">{o.leave_a.leave_type} · {fmtDate(o.leave_a.start_date)} → {fmtDate(o.leave_a.end_date)}</td>
                      <td className="p-2 font-mono">{o.leave_b.leave_type} · {fmtDate(o.leave_b.start_date)} → {fmtDate(o.leave_b.end_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

const EmployeeResults = ({ data, onMerge, merging }) => {
  const s = data.summary || {};
  const sections = [
    { key: 'by_employee_code', label: 'Employee Code', tone: 'rose' },
    { key: 'by_name', label: 'Name', tone: 'rose' },
    { key: 'by_passport', label: 'Passport Number', tone: 'amber' },
    { key: 'by_emirates_id', label: 'Emirates ID', tone: 'amber' },
    { key: 'by_visa', label: 'Visa Number', tone: 'amber' },
    { key: 'by_labour_card', label: 'Labour Card', tone: 'amber' },
  ];
  const hasAny = sections.some(s2 => (data[s2.key] || []).length > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2 text-xs">
        <Tile label="Scanned" v={s.total_employees_scanned} tone="violet" />
        <Tile label="Name collisions" v={s.name_collisions} tone="rose" />
        <Tile label="Passport collisions" v={s.passport_collisions} tone="amber" />
        <Tile label="EID collisions" v={s.eid_collisions} tone="amber" />
      </div>
      {!hasAny ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
          ✓ No duplicate employees detected across name, passport, EID, visa, or labour card.
        </div>
      ) : (
        sections.map(sec => {
          const items = data[sec.key] || [];
          if (items.length === 0) return null;
          return (
            <Section key={sec.key} title={`Match by ${sec.label} (${items.length})`} tone={sec.tone}>
              <table className="w-full text-xs">
                <thead className={`text-[10px] uppercase ${sec.tone === 'rose' ? 'bg-rose-50' : 'bg-amber-50'}`}>
                  <tr>
                    <th className="text-left p-2">Value</th>
                    <th className="text-left p-2">Employees</th>
                    <th className="text-left p-2 w-[180px]">Merge & Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-t align-top" data-testid={`emp-${sec.key}-row-${i}`}>
                      <td className="p-2 font-mono break-all max-w-[200px]">{it.value}</td>
                      <td className="p-2">
                        <div className="flex flex-wrap gap-1">
                          {it.employees.map(e => (
                            <span key={e.id} className={`text-[11px] px-1.5 py-0.5 rounded border ${e.is_active ? 'bg-white border-gray-300' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
                              {e.employee_code} · {e.name}
                              {!e.is_active && <span className="ml-1 text-[9px] uppercase">({e.employment_status})</span>}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2">
                        <MergeControls
                          employees={it.employees}
                          rowKey={`${sec.key}-${i}`}
                          onMerge={onMerge}
                          merging={merging}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          );
        })
      )}
    </div>
  );
};

const MergeControls = ({ employees, rowKey, onMerge, merging }) => {
  // Pick a sensible default: the active employee as "kept" if exactly one is
  // active, else the lower-numbered employee_code (older / canonical record).
  const ranked = [...employees].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    return (a.employee_code || '').localeCompare(b.employee_code || '');
  });
  const [keptId, setKeptId] = useState(ranked[0]?.id || '');
  const [rejectedId, setRejectedId] = useState(ranked[1]?.id || '');

  const kept = employees.find(e => e.id === keptId);
  const rejected = employees.find(e => e.id === rejectedId);
  const isBusy = merging === `${rejectedId}->${keptId}`;
  const valid = keptId && rejectedId && keptId !== rejectedId;

  if (employees.length < 2) return <span className="text-[10px] text-gray-400">—</span>;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1 text-[10px]">
        <span className="text-gray-500 w-8">Keep</span>
        <select
          className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-[10px]"
          value={keptId}
          onChange={(e) => setKeptId(e.target.value)}
          data-testid={`merge-keep-${rowKey}`}
        >
          {employees.map(e => (
            <option key={e.id} value={e.id}>{e.employee_code} · {e.name}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1 text-[10px]">
        <span className="text-gray-500 w-8">Drop</span>
        <select
          className="flex-1 border border-gray-300 rounded px-1 py-0.5 text-[10px]"
          value={rejectedId}
          onChange={(e) => setRejectedId(e.target.value)}
          data-testid={`merge-drop-${rowKey}`}
        >
          {employees.filter(e => e.id !== keptId).map(e => (
            <option key={e.id} value={e.id}>{e.employee_code} · {e.name}</option>
          ))}
        </select>
      </div>
      <Button
        size="sm"
        variant="destructive"
        disabled={!valid || isBusy}
        onClick={() => onMerge(rejected, kept)}
        className="h-6 px-2 text-[10px] w-full"
        data-testid={`merge-btn-${rowKey}`}
        title="Transfer all HR records from the dropped employee to the kept one, then delete the dropped row."
      >
        {isBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
        Merge & Delete
      </Button>
    </div>
  );
};

const Tile = ({ label, v, tone }) => {
  const cls = {
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
  }[tone] || 'border-gray-200 bg-gray-50 text-gray-700';
  return (
    <div className={`rounded-md border px-2 py-1.5 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wide leading-tight">{label}</p>
      <p className="text-lg font-bold leading-tight">{v || 0}</p>
    </div>
  );
};

const Section = ({ title, tone, children }) => {
  const cls = tone === 'rose' ? 'border-rose-200' : tone === 'amber' ? 'border-amber-200' : 'border-gray-200';
  return (
    <div className={`border ${cls} rounded-lg overflow-hidden`}>
      <p className={`px-3 py-2 text-xs font-semibold ${tone === 'rose' ? 'text-rose-700 bg-rose-50' : tone === 'amber' ? 'text-amber-700 bg-amber-50' : 'text-gray-700 bg-gray-50'}`}>
        <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
        {title}
      </p>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
};

export default DuplicateScannerDialog;
